import {Link} from 'react-router-dom';

import type {BallotStatus, BallotSummaryResource} from '../../shared/ballotTypes';
import {ErrorNotice} from '../../shared/components/notice';
import {SYSTEM_LABEL} from '../../shared/format';
import {PaperButton, Toggle} from '../../shared/sticker/stickers';
import {actionsFor} from './statusActions';

const PILL_LABEL: Record<BallotStatus, string> = {
    draft: 'Draft',
    open: 'Open',
    closed: 'Closed',
    revealed: 'Revealed',
};

const summaryLine = (b: BallotSummaryResource): string =>
    `${SYSTEM_LABEL[b.system]} · ${b.option_count} options · ${b.voted_count} of ${b.voter_count} voted · code ${b.join_code}`;

export type VotesProps = {
    state: 'loading' | 'error' | 'ready';
    error?: unknown;
    ballots: BallotSummaryResource[];
    canSeeAll: boolean;
    all: boolean;
    creating: boolean;
    busyId: string | null;
    onAllChange: (all: boolean) => void;
    onNew: () => void;
    onPresent: (id: string) => void;
    onDuplicate: (id: string) => void;
    onDelete: (id: string) => void;
};

const Skeleton = () => (
    <div className="bt-votes__list" role="status" aria-label="Loading votes">
        {[0, 1, 2].map((n) => (
            <div key={n} className="bt-votes__row bt-votes__row--skeleton" />
        ))}
    </div>
);

export const Votes = (props: VotesProps) => {
    const {state, ballots} = props;
    const newButton = (
        <PaperButton onClick={props.onNew} disabled={props.creating}>
            {props.creating ? 'Creating...' : 'New vote'}
        </PaperButton>
    );
    return (
        <div className="bt-votes">
            <div className="bt-votes__head">
                <h1 className="bt-votes__title">Your votes</h1>
                {props.canSeeAll && (
                    <Toggle
                        label="Everyone's votes"
                        checked={props.all}
                        onChange={props.onAllChange}
                    />
                )}
                <span className="bt-votes__new">{newButton}</span>
            </div>
            {state === 'loading' && <Skeleton />}
            {state === 'error' && (
                <ErrorNotice title="Could not load your votes" error={props.error} />
            )}
            {state === 'ready' && ballots.length === 0 && (
                <div className="bt-panel bt-votes__empty">
                    <p>No votes yet. Create one to get started.</p>
                    {newButton}
                </div>
            )}
            {state === 'ready' && ballots.length > 0 && (
                <ul className="bt-votes__list">
                    {ballots.map((b) => (
                        <li key={b.id} className="bt-votes__row">
                            <span className={`bt-pill bt-pill--${b.status}`}>
                                {PILL_LABEL[b.status]}
                            </span>
                            <div className="bt-votes__info">
                                <b>{b.title}</b>
                                <span>{summaryLine(b)}</span>
                            </div>
                            <div className="bt-votes__actions">
                                {actionsFor(b.status, 0).map((action) => (
                                    <RowAction key={action} action={action} ballot={b} {...props} />
                                ))}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const LABEL = {
    edit: 'Edit',
    present: 'Open presenter',
    reveal: 'Reveal',
    results: 'Results',
    duplicate: 'Duplicate',
    delete: 'Delete',
} as const;

const RowAction = ({
    action,
    ballot,
    busyId,
    onPresent,
    onDuplicate,
    onDelete,
}: VotesProps & {action: keyof typeof LABEL; ballot: BallotSummaryResource}) => {
    if (action === 'edit') {
        return (
            <Link
                className="bt-button bt-button--ghost bt-button--small"
                to={`/admin/ballots/${ballot.id}`}
            >
                {LABEL.edit}
            </Link>
        );
    }
    const handlers = {
        present: () => onPresent(ballot.id),
        reveal: () => onPresent(ballot.id),
        results: () => onPresent(ballot.id),
        duplicate: () => onDuplicate(ballot.id),
        delete: () => onDelete(ballot.id),
    };
    return (
        <PaperButton
            variant="ghost"
            className="bt-button--small"
            disabled={busyId === ballot.id}
            onClick={handlers[action]}
        >
            {LABEL[action]}
        </PaperButton>
    );
};
